import {
    AnswerStatus,
    type AnswerValueInput,
    BooleanEnum,
    type GetQuestionnaireOverviewDataQuery,
    type GetQuestionQuery,
    type GetQuestionsQuery,
    type QuestionCreateInput,
    type QuestionMetadataInput,
    QuestionProof,
    QuestionType,
    type QuestionUpdateInput,
} from "@generated/client/graphql";

import { convertBooleanEnumToBoolean } from "@app/lib/utils";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { getRemainingWeeks } from "@app/usecases/DashboardUseCases";
import { isEqual } from "lodash";
import { type UpsertQuestionInput } from "./components/OverviewTab/types";

export function computeQuestionInput(
    question?: GetQuestionQuery["question"],
    withProof?: boolean,
    withNaOption?: boolean,
): UpsertQuestionInput {
    return {
        nameMultiLocale: {
            en: question?.nameMultiLocale?.en ?? "",
            fr: question?.nameMultiLocale?.fr ?? "",
        },
        descriptionMultiLocale: {
            en: question?.descriptionMultiLocale?.en ?? "",
            fr: question?.descriptionMultiLocale?.fr ?? "",
        },
        type: question?.type ?? QuestionType.Text,
        unit: question?.metadata?.unit ?? undefined,
        options:
            question?.metadata?.options?.map((o) => ({
                name: {
                    en: o?.name?.en ?? "",
                    fr: o?.name?.fr ?? "",
                },
                proof: withProof
                    ? (o?.proof ?? QuestionProof.NotAllowed)
                    : undefined,
            })) ?? [],
        dependsOn: {
            questionId: question?.metadata?.dependsOn?.questionId,
            displayIfSelected: question?.metadata?.dependsOn?.displayIfSelected,
        },
        proof: withProof
            ? (question?.metadata?.proof ?? QuestionProof.NotAllowed)
            : undefined,
        enableNaOption:
            withNaOption != undefined
                ? (question?.metadata?.enableNaOption ?? true)
                : undefined,
        isRequired: convertBooleanEnumToBoolean(question?.isRequired),
    };
}

export function computeMetadata(question: {
    type: QuestionType;
    options?: { name: Record<string, string>; proof?: QuestionProof }[];
    unit?: string | null;
    parentQuestionId?: string;
    displayIfSelected?: number[];
    proof?: QuestionProof;
    enableNaOption?: boolean;
}): QuestionMetadataInput | null {
    let metadata: QuestionMetadataInput | null = null;
    if (isSelectType(question.type) && question?.options) {
        metadata = {
            options: question.options?.map((o) => ({
                name: { en: o.name["en"], fr: o.name["fr"] },
                proof: o.proof ?? QuestionProof.NotAllowed,
            })),
        };
    }

    if (question.proof) {
        metadata = {
            ...(metadata ?? {}),
            proof: question.proof,
        };
    }

    if (question.type === QuestionType.Value) {
        metadata = {
            ...(metadata ?? {}),
            unit: question.unit,
        };
    }

    if (question.parentQuestionId) {
        metadata = {
            ...(metadata ?? {}),
            dependsOn: {
                questionId: question.parentQuestionId,
                displayIfSelected: question.displayIfSelected ?? [],
            },
        };
    }

    if (question.enableNaOption != undefined) {
        metadata = {
            ...(metadata ?? {}),
            enableNaOption: question.enableNaOption,
        };
    }

    return metadata;
}

export function isQuestionCreateInput(
    question: Partial<QuestionCreateInput>,
): question is QuestionCreateInput {
    return (
        typeof question.name === "string" &&
        typeof question.type !== "undefined"
    );
}

export function isQuestionType(value: string): value is QuestionType {
    return Object.values(QuestionType).includes(value as QuestionType);
}

export function canSubmitQuestion(
    input: UpsertQuestionInput,
    locale: SupportedLocales,
    question?: Question,
    { withProof = false }: { withProof?: boolean } = {},
) {
    const isUpdateScenario = question !== undefined;
    if (isUpdateScenario) {
        const typeChanged = input.type !== question?.type;
        const descriptionChanged = !isEqual(
            input.descriptionMultiLocale,
            question?.descriptionMultiLocale ?? { en: "", fr: "" },
        );

        const nameChanged = !isEqual(
            input.nameMultiLocale,
            question?.nameMultiLocale,
        );
        const unitChanged =
            input.type === QuestionType.Value &&
            input.unit !== question?.metadata?.unit;

        const shouldCheckOptions =
            input.type === QuestionType.SingleSelect ||
            input.type === QuestionType.MultiSelect;

        const optionNamesChanged = shouldCheckOptions
            ? input?.options?.length > 0 &&
              !isEqual(
                  input.options?.map((o) => o?.name?.[locale]),
                  question?.metadata?.options?.map((o) => o.name?.[locale]) ??
                      [],
              )
            : false;

        const optionCountChanged = shouldCheckOptions
            ? input?.options?.length !== question?.metadata?.options?.length
            : false;

        const optionProofsChanged =
            withProof && shouldCheckOptions
                ? input?.options?.length > 0 &&
                  !isEqual(
                      input.options?.map((o) => o?.proof),
                      question?.metadata?.options?.map((o) => o.proof) ?? [],
                  )
                : false;

        const optionsChanged =
            optionNamesChanged || optionProofsChanged || optionCountChanged;

        const dependsOnQuestionIdChange = !isEqual(
            input.dependsOn?.questionId,
            question?.metadata?.dependsOn?.questionId,
        );
        const dependsOnQuestionAnswersChange = !isEqual(
            input.dependsOn?.displayIfSelected,
            question?.metadata?.dependsOn?.displayIfSelected,
        );

        const proofChanged =
            withProof && !isEqual(input.proof, question?.metadata?.proof);

        const enableNaOptionChanged =
            input.enableNaOption !== question?.metadata?.enableNaOption;

        const isRequiredChanged =
            input.isRequired !==
            convertBooleanEnumToBoolean(question?.isRequired);

        return (
            typeChanged ||
            descriptionChanged ||
            nameChanged ||
            unitChanged ||
            optionsChanged ||
            dependsOnQuestionIdChange ||
            dependsOnQuestionAnswersChange ||
            proofChanged ||
            enableNaOptionChanged ||
            isRequiredChanged
        );
    } else {
        const hasType = input.type !== undefined;
        const hasName = input.nameMultiLocale.en || input.nameMultiLocale.fr;

        const shouldCheckOptions =
            input.type === QuestionType.SingleSelect ||
            input.type === QuestionType.MultiSelect;
        const optionsCheck = shouldCheckOptions
            ? input.options?.length > 0 &&
              input.options.some((o) => o.name.en || o.name.fr)
            : true;

        return hasType && hasName && optionsCheck;
    }
}

export function isSelectType(questionType?: QuestionType) {
    return (
        !!questionType &&
        [QuestionType.SingleSelect, QuestionType.MultiSelect].includes(
            questionType,
        )
    );
}
type Question = Exclude<GetQuestionQuery["question"], null>;

export function shouldDisplayQuestion(
    question: Question,
    parentQuestion?: Question,
) {
    if (parentQuestion == null) {
        return true;
    }
    let shouldDisplay = true;
    const isDependent = question?.metadata?.dependsOn?.questionId != null;
    if (isDependent) {
        const options =
            parentQuestion?.answer?.value?.options?.filter((index: number) =>
                question?.metadata?.dependsOn?.displayIfSelected?.includes(
                    index,
                ),
            ) ?? [];
        shouldDisplay = options.length > 0;
    }
    return shouldDisplay;
}

export const Status = {
    Validated: "validated",
    InProgress: "inProgress",
    Todo: "todo",
} as const;

export function isAnswered(value?: AnswerValueInput) {
    return (
        !!value &&
        (("text" in value && value.text !== "" && value.text != null) ||
            ("num" in value && value.num != undefined) ||
            ("options" in value && !!value.options?.length) ||
            ("table" in value && !!value.table?.length) ||
            ("na" in value && value.na != null))
    );
}

export const computeStatus = ({
    status,
    value,
}: {
    status: AnswerStatus;
    value?: AnswerValueInput;
}) => {
    if (status === AnswerStatus.Validated) {
        return Status.Validated;
    }

    if (isAnswered(value)) {
        return Status.InProgress;
    }

    return Status.Todo;
};

export function optimisticUpdateQuestionInMany(
    { id, input }: { id: string; input: QuestionUpdateInput },
    previousQuery: GetQuestionnaireOverviewDataQuery,
) {
    const questions = previousQuery.getQuestionnaireOverviewData.questions.map((question) => {
        if (id === question.id)
            return {
                ...question,
                ...input,
            } as GetQuestionnaireOverviewDataQuery["getQuestionnaireOverviewData"]["questions"][number];
        return question;
    });
    return {
        ...previousQuery,
        questions,
    };
}

export function optimisticUpdateQuestion(
    { input }: { input: QuestionUpdateInput },
    previousQuery?: GetQuestionQuery,
) {
    if (!previousQuery) return previousQuery;
    const { name, type, activation, ...updatedQuestion } = input;

    return {
        ...previousQuery,
        question: {
            ...previousQuery.question,
            ...updatedQuestion,
            ...(name ? { name } : {}),
            ...(type ? { type } : {}),
            ...(activation ? { activation } : {}),
            ...(input.isRequired
                ? { isRequired: BooleanEnum.True }
                : { isRequired: BooleanEnum.False }),
        },
    };
}

export function getQuestionnaireStats<
    T extends
        | {
              deadline?: Date | null;
              questions: {
                  ownerId?: string | null;
                  answer?: {
                      status?: AnswerStatus;
                  } | null;
              }[];
          }
        | undefined,
>(questionnaire: T) {
    if (!questionnaire) return;

    const remainingWeeks = questionnaire.deadline
        ? getRemainingWeeks(new Date(questionnaire.deadline))
        : undefined;

    const validatedQuestionsCount = questionnaire?.questions?.filter(
        (question) => question.answer?.status === AnswerStatus.Validated,
    ).length;
    const validatedQuestionsPercentage =
        questionnaire?.questions?.length > 0
            ? Math.round(
                  (validatedQuestionsCount / questionnaire?.questions?.length) *
                      100,
              )
            : 0;

    return {
        totalQuestions: questionnaire?.questions?.length,
        validatedQuestions: {
            count: validatedQuestionsCount,
            percentage: validatedQuestionsPercentage,
        },
        nonValidatedQuestions: {
            count: questionnaire?.questions?.length - validatedQuestionsCount,
            percentage: 100 - validatedQuestionsPercentage,
        },
        deadline: remainingWeeks ? { remainingWeeks } : undefined,
    };
}

export function isQuestionGenerating(
    question: GetQuestionnaireOverviewDataQuery["getQuestionnaireOverviewData"]["questions"][number] | GetQuestionsQuery["questions"][number],
) {
    const acceptableTimeToGenerate = 9000 * 10; // 90 seconds

    return (
        !!question?.answer?.aiSuggestion?.isGenerating &&
        (question?.answer?.lastGeneratedAt == null ||
            acceptableTimeToGenerate >
                Date.now() -
                    new Date(question?.answer?.lastGeneratedAt).getTime())
    );
}

export const filterQuestionsByDependency = (
    questionsToFilter?: GetQuestionsQuery["questions"]
) => {
    if (!questionsToFilter) return [];

    return questionsToFilter.filter((question) => {
        if (!question.metadata?.dependsOn) return true;

        const parentQuestion =
            questionsToFilter?.find(
                (q) => q.id === question.metadata?.dependsOn?.questionId,
            ) ??
            questionsToFilter?.find(
                (q) => q.cmsId === question.metadata?.dependsOn?.questionId,
            );
        if (!parentQuestion) return false;

        return question.metadata?.dependsOn?.displayIfSelected?.some(
            (requiredAnswerIndex) =>
                parentQuestion.answer?.value?.options?.includes(
                    requiredAnswerIndex,
                ),
        );
    });
};

export const filterOverviewDataQuestionsByDependency = (
    questionsToFilter?: GetQuestionnaireOverviewDataQuery["getQuestionnaireOverviewData"]["questions"]
) => {
    if (!questionsToFilter) return [];

    return questionsToFilter.filter((question) => {
        if (!question.metadata?.dependsOn) return true;

        const parentQuestion =
            questionsToFilter?.find(
                (q) => q.id === question.metadata?.dependsOn?.questionId,
            ) ??
            questionsToFilter?.find(
                (q) => q.cmsId === question.metadata?.dependsOn?.questionId,
            );
        if (!parentQuestion) return false;

        return question.metadata?.dependsOn?.displayIfSelected?.some(
            (requiredAnswerIndex) =>
                parentQuestion.answer?.value?.options?.includes(
                    requiredAnswerIndex,
                ),
        );
    });
};
